import { Tag, Tooltip } from 'antd';
import { useEffect, useState } from 'react';

import { CliTerminalSession } from 'types/cli';

interface CliSessionStatusProps {
  session: CliTerminalSession;
}

const MINUTE = 60 * 1000;
const EXPIRING_SOON = 30 * MINUTE;
const TICK = 15 * 1000;

type Health = 'active' | 'expiring' | 'expired';

const formatRemaining = (remaining: number) => {
  const minutes = Math.floor(remaining / MINUTE);
  if (minutes < 1) return 'under a minute';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

const describeSession = (
  expiresAt: string,
  now: number,
): { health: Health; label: string } => {
  const remaining = new Date(expiresAt).getTime() - now;
  if (Number.isNaN(remaining)) return { health: 'active', label: '' };
  if (remaining <= 0) return { health: 'expired', label: 'session expired' };
  const health: Health = remaining <= EXPIRING_SOON ? 'expiring' : 'active';
  return { health, label: `expires in ${formatRemaining(remaining)}` };
};

function CliSessionStatus({ session }: Readonly<CliSessionStatusProps>) {
  const [now, setNow] = useState(() => Date.now());

  // The clock lives here rather than in the dock: ticking it there would re-render every open
  // transcript every 15 seconds for a label none of them care about.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), TICK);
    return () => clearInterval(timer);
  }, []);

  const { health, label } = describeSession(session.expiresAt, now);
  const absolute = new Date(session.expiresAt).toLocaleString();

  return (
    <span className="CliDock__session">
      <Tooltip
        title={
          health === 'expired' ? `Expired ${absolute}` : `Expires ${absolute}`
        }
      >
        <span className={`CliDock__dot CliDock__dot--${health}`} />
      </Tooltip>
      {session.username}
      <Tag>{session.permission}</Tag>
      {label && (
        <Tooltip title={absolute}>
          <span className={`CliDock__expiry CliDock__expiry--${health}`}>
            {label}
          </span>
        </Tooltip>
      )}
    </span>
  );
}

export default CliSessionStatus;
